import { useState, useRef, useEffect } from 'preact/hooks';
import { Fragment } from 'preact';
import getProductType from '../../utils/getProductType';
import { DayPicker, useDayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import dayjs from 'dayjs';
import { Text, withText } from 'preact-i18n';
import Modal from '../Modal';
import DatesControls from '../DatePicker/components/DatesControls';
import stringToDate from '../../utils/stringToDate';
import { selectIcon } from '../../templates/place';
import flexibleCalendarImg from '../../../assets/images/flexible-calendar.webp';
import flexibleCalendarFlatImg from '../../../assets/images/flexible-calendar-flat.webp';
import flexibleCalendarIcon from '../../../assets/images/ic-flexible-calendar.webp';
import mixpanelTracker from '../../utils/mixpanelTracker';
import differenceInDays from 'date-fns/differenceInDays';

const CALENDAR_EVENT = 'Calendar Button Clicked';
const TAB_TO_SECTION = {
  oneWay: 'Single',
  round: 'Round',
  openTicket: 'Open',
};

const ReturnOpenSwitch = ({ returnIsOpen, handleToggleOpenReturn, flatVariant }) => {
  const switchIconSrc = flatVariant ? flexibleCalendarFlatImg : flexibleCalendarIcon;
  return (
    <label className="udp-open-return">
      <img src={switchIconSrc} className="udp-open-return__icon" alt="" aria-hidden="true" />
      <div className="udp-open-return__text">
        <span className="udp-open-return__title">
          <Text id="label.open_return" />
        </span>
        <span className="udp-open-return__subtitle">
          <Text id="label.open_return_subtitle" />
        </span>
      </div>
      <div
        className={`udp-open-return__switch${returnIsOpen ? ' udp-open-return__switch--on' : ''}`}
        role="switch"
        aria-checked={returnIsOpen}
        tabIndex={0}
        onClick={handleToggleOpenReturn}
      >
        <div className="udp-open-return__knob" />
      </div>
    </label>
  );
};

/**
 * Custom month caption: renders  ‹  MMMM YYYY  ›  in a single row.
 * Defined outside the component so its identity is stable across re-renders.
 */
const MonthCaption = ({ calendarMonth }) => {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();
  return (
    <div className="udp-calendar-header">
      <button
        type="button"
        className="udp-nav-btn"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        disabled={!previousMonth}
        aria-label="previous month"
      >
        ‹
      </button>
      <span className="udp-month-label">{dayjs(calendarMonth.date).format('MMMM YYYY')}</span>
      <button
        type="button"
        className="udp-nav-btn"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        disabled={!nextMonth}
        aria-label="next month"
      >
        ›
      </button>
    </div>
  );
};

// Hide the default nav — navigation is rendered inside MonthCaption
const HiddenNav = () => null;

/**
 * Custom DayButton for react-day-picker. When `badgeText` is set it overlays a
 * small label on the selected day to indicate "Ida" or "Regreso" in round-trip
 * mode when one leg is an open ticket.
 */
const DayButtonWithBadge = ({ modifiers, children, badgeText, ...buttonProps }) => (
  <button {...buttonProps}>
    {children}
    {modifiers.selected && badgeText && <span className="udp-day-badge">{badgeText}</span>}
  </button>
);

/**
 * Unified date picker that replaces the two separate Datepicker components when the
 * `unifiedDatePicker` feature is active.
 */
const UnifiedDatePicker = ({
  departureDate,
  returnDate,
  returnPlaceholder,
  tripType,
  dateFormat,
  maxDaysSearch,
  openTicketText,
  showOpenTicket,
  flatVariant,
  error,
  showMundialButton,
  showTomorrowWithMundial,
  locale,
  onTripTypeChange,
  onDepartureChange,
  onReturnChange,
  onReturnOpenTicket,
  onDepartureOpenTicket,
  onFocus,
  onContinue,
  departureBadge,
  openTicketLabel,
  openCalendar,
  buttonBg,
  hasPassengersModal,
}) => {
  const compactUsedRef = useRef(false);
  const wrapperRef = useRef(null);
  const pendingContinueRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [modalTab, setModalTab] = useState('oneWay');
  // Internal Date objects for the inline calendar
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Close modal when clicking outside the wrapper (desktop only)
  useEffect(() => {
    if (!isOpen || getProductType() === 'web-mobile') return;

    const handleOutsideClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeModal();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Sync calendar selection when external dates are cleared while modal is open
  useEffect(() => {
    if (!isOpen) return;
    setStartDate(toDateObj(departureDate !== openTicketText ? departureDate : null));
    setEndDate(toDateObj(returnDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate, returnDate]);

  useEffect(() => {
    if (!openCalendar) return;
    openModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCalendar]);

  // Deferred onContinue: fires once departureDate propagates after handleContinue sets it
  useEffect(() => {
    if (!pendingContinueRef.current || !departureDate) return;
    pendingContinueRef.current = false;
    if (onContinue) onContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate]);

  // ─── Tab state ─────────────────────────────────────────────────────────────

  const deriveTab = () => {
    if (departureDate === openTicketText || tripType === 'openTicket') return 'openTicket';
    if (tripType === 'round' || returnDate) return 'round';
    return 'oneWay';
  };

  // Currently displayed month (controlled navigation)
  const [currentMonth, setCurrentMonth] = useState(null);

  // ─── Date helpers ──────────────────────────────────────────────────────────

  const toDateObj = str => (str ? stringToDate(str, dateFormat) : null);
  const toStr = date => (date ? dayjs(date).format(dateFormat.toUpperCase()) : '');

  const today = dayjs()
    .startOf('day')
    .toDate();
  const suggestedReturnDate = dayjs()
    .add(3, 'day')
    .startOf('day')
    .toDate();
  const maxDate = dayjs()
    .add(maxDaysSearch, 'day')
    .toDate();

  // ─── Display value for the trigger ────────────────────────────────────────

  const showCompact = !departureDate && !compactUsedRef.current;

  // ─── Open / close modal ────────────────────────────────────────────────────

  const openModal = forceTab => {
    if (onFocus) onFocus();
    const tab = forceTab || deriveTab();
    const start = departureDate
      ? toDateObj(departureDate !== openTicketText ? departureDate : null)
      : null;
    const end = returnDate ? toDateObj(returnDate !== openTicketText ? returnDate : null) : null;
    setModalTab(tab);
    setStartDate(start);
    setEndDate(end);
    setCurrentMonth(start || today);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentMonth(null);
  };

  const handleInputClick = fromReturn => {
    // Opening from departure → single mode to avoid accidentally changing return
    // Opening from return → range mode to allow picking a new return date
    openModal(fromReturn && departureDate !== openTicketText ? 'round' : null);
  };

  // ─── Compact date quick-selects ────────────────────────────────────────────

  const handleQuickDate = daysOffset => {
    mixpanelTracker.trackEvent(CALENDAR_EVENT, {
      type: daysOffset === 0 ? 'today' : 'tomorrow',
      new_calendar: true,
    });
    const date = dayjs()
      .add(daysOffset, 'day')
      .toDate();
    compactUsedRef.current = true;
    if (returnDate) onReturnChange('');
    onTripTypeChange('oneWay');
    onDepartureChange(toStr(date));
  };

  const handleMundialClick = () => {
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'mundial', new_calendar: true });
    compactUsedRef.current = true;
    openModal('oneWay');
    setCurrentMonth(new Date(2026, 5, 1)); // June 2026
  };

  const handleCompactDateClick = () => {
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'choose', new_calendar: true });
    openModal();
  };

  // ─── Open ticket handlers (defined before handleTabChange to avoid TDZ) ────

  const handleOpenDepartureTicket = () => {
    mixpanelTracker.trackEvent('Open Ticket Selected', { type: 'departure' });
    compactUsedRef.current = true;
    if (returnDate !== openTicketText) {
      onReturnChange('');
    }
    onTripTypeChange('openTicket');
    onDepartureChange(openTicketText);
    if (onDepartureOpenTicket) onDepartureOpenTicket();
  };

  const handleOpenReturnTicket = () => {
    mixpanelTracker.trackEvent('Open Ticket Selected', { type: 'return' });
    compactUsedRef.current = true;
    onTripTypeChange('round');
    if (startDate) onDepartureChange(toStr(startDate));
    onReturnChange(openTicketText);
    if (onReturnOpenTicket) onReturnOpenTicket();
  };

  const handleToggleOpenReturn = () => {
    mixpanelTracker.trackEvent('Open Return Ticket Toggled', {
      enabled: !returnIsOpen,
      new_calendar: true,
    });
    if (returnIsOpen) {
      onReturnChange('');
      onTripTypeChange(startDate ? 'round' : 'oneWay');
    } else {
      handleOpenReturnTicket();
    }
  };

  // ─── Tab change inside modal ───────────────────────────────────────────────

  const handleTabChange = tab => {
    mixpanelTracker.trackEvent('Search Calendar Tab Clicked', {
      section: TAB_TO_SECTION[tab],
      new_calendar: true,
    });
    if (tab === 'openTicket') {
      setModalTab(tab);
      handleOpenDepartureTicket();
      return;
    }
    setModalTab(tab);
    // When switching to one-way, carry the range start as the departure date
    if (tab === 'oneWay' && departureDate !== openTicketText && returnDate) {
      compactUsedRef.current = true;
      onTripTypeChange('oneWay');
      onDepartureChange(toStr(startDate));
      setCurrentMonth(startDate);
      if (returnDate) onReturnChange('');
    }
    if (tab === 'oneWay' && departureDate === openTicketText) {
      compactUsedRef.current = true;
      onTripTypeChange('oneWay');
      onDepartureChange(toStr(today));
      setCurrentMonth(today);
      if (returnDate) onReturnChange('');
    }
    // Round: no departure yet, or any open-ticket leg → recommended range (single branch, same outcome as before)
    if (
      tab === 'round' &&
      (!departureDate || departureDate === openTicketText || returnDate === openTicketText)
    ) {
      compactUsedRef.current = true;
      onTripTypeChange('round');
      onDepartureChange(toStr(today));
      onReturnChange(toStr(suggestedReturnDate));
      setStartDate(today);
      setEndDate(suggestedReturnDate);
      setCurrentMonth(today);
      return;
    }
  };

  // ─── Calendar date selection handlers ─────────────────────────────────────

  const isDesktop = getProductType() !== 'web-mobile';

  const handleRoundOpenSlotChange = date => {
    if (!date) return;
    const hadReturnOpen = returnDate === openTicketText;
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'personalized', tab: 'round', new_calendar: true });
    compactUsedRef.current = true;
    onTripTypeChange('round');

    // Return was "open ticket": clear it and go back to range mode (first day = start of range).
    if (hadReturnOpen) {
      onReturnChange('');
      onDepartureChange(toStr(date));
      setStartDate(date);
      setEndDate(null);
      return;
    }

    onDepartureChange(toStr(date));
    if (isDesktop) {
      closeModal();
    }
  };

  const handleSingleChange = date => {
    if (!date) return;
    mixpanelTracker.trackEvent(CALENDAR_EVENT, {
      type: 'personalized',
      tab: 'oneWay',
      new_calendar: true,
    });
    compactUsedRef.current = true;
    onTripTypeChange('oneWay');
    if (returnDate) onReturnChange('');
    onDepartureChange(toStr(date));
  };

  const handleSaveRangeDates = (start, end) => {
    compactUsedRef.current = true;
    onTripTypeChange('round');
    onDepartureChange(toStr(start));
    onReturnChange(toStr(end));
  };

  // react-day-picker range: onSelect receives { from, to } | undefined.
  // On the first click the library sets from === to (same day); we treat that
  // as "start only" so the user can still pick the return date.
  const handleRangeChange = range => {
    const start = range?.from || null;
    const rawEnd = range?.to || null;
    const end = rawEnd && start && rawEnd.getTime() !== start.getTime() ? rawEnd : null;
    setStartDate(start);
    setEndDate(end);
    if (start && end) {
      mixpanelTracker.trackEvent(CALENDAR_EVENT, {
        type: 'personalized',
        tab: 'round',
        new_calendar: true,
        start: start.toISOString(),
        end: end.toISOString(),
        difference: differenceInDays(end, start),
      });
      handleSaveRangeDates(start, end);
    }
  };

  const handleTodayClick = () => {
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'today', new_calendar: true });
    if (modalTab === 'oneWay') {
      handleSingleChange(today);
    } else if (modalTab === 'round') {
      handleSaveRangeDates(today, suggestedReturnDate);
    }
  };

  const handleContinue = () => {
    mixpanelTracker.trackEvent('Calendar Continue Clicked', {
      tab: TAB_TO_SECTION[modalTab],
      new_calendar: true,
    });
    closeModal();
    if (!departureDate && modalTab === 'oneWay') {
      pendingContinueRef.current = true;
      onDepartureChange(toStr(startDate || today));
      return;
    }
    if (!returnDate && !departureDate && modalTab === 'round') {
      handleSaveRangeDates(startDate, suggestedReturnDate);
    }
    if (onContinue) onContinue();
  };

  // ─── Clean buttons ─────────────────────────────────────────────────────────

  const handleCleanDeparture = e => {
    e.stopPropagation();
    compactUsedRef.current = false;
    onDepartureChange('');
    if (returnDate) onReturnChange('');
    setStartDate(null);
    setEndDate(null);
    setModalTab('oneWay');
    onTripTypeChange('oneWay');
    closeModal();
  };

  // ─── Continue footer date display ─────────────────────────────────────────

  const fmtDate = date => dayjs(date).format('D MMM');

  // True while the user has clicked the first day of a new range but not the second yet
  const isSelectingRange = modalTab === 'round' && startDate !== null && endDate === null;

  const footerDeparture = (() => {
    if (modalTab === 'openTicket') return openTicketText;
    if (departureDate === openTicketText) return openTicketText;
    if (startDate) return fmtDate(startDate);
    if (departureDate) return fmtDate(toDateObj(departureDate)) || departureDate;
    return null;
  })();

  const footerReturn = (() => {
    if (returnDate === openTicketText) return openTicketText;
    if (endDate) return fmtDate(endDate);
    // Hide the previous return date while the user is picking a new range start
    if (isSelectingRange) return null;
    if (returnDate) return fmtDate(toDateObj(returnDate)) || returnDate;
    return null;
  })();


  // In round mode, if one leg is open ticket the calendar uses single-select.
  // The picked day always applies to departure: open departure + concrete return is invalid.
  const departureIsOpen = departureDate === openTicketText;
  const returnIsOpen = returnDate === openTicketText;
  const roundWithOpenSlot = modalTab === 'round' && (departureIsOpen || returnIsOpen);

  // ─── Badge text for the custom DayButton ──────────────────────────────────
  const selectedDate = roundWithOpenSlot ? startDate : departureIsOpen ? endDate : startDate;
  const dayBadgeText = roundWithOpenSlot && selectedDate ? departureBadge : null;

  // ─── Shared DayPicker props ────────────────────────────────────────────────

  const sharedDayPickerProps = {
    weekStartsOn: 1,
    disabled: [{ before: today }, { after: maxDate }],
    endMonth: maxDate,
    modifiers: { suggestedDay: !startDate ? today : undefined },
    modifiersClassNames: { suggestedDay: 'udp-suggested-day' },
    month: currentMonth || today,
    onMonthChange: month => {
      mixpanelTracker.trackEvent('Calendar Month Changed', {
        month: dayjs(month).month() + 1,
        new_calendar: true,
      });
      setCurrentMonth(month);
    },
    formatters: {
      formatCaption: date => dayjs(date).format('MMMM YYYY'),
      formatWeekdayName: date => dayjs(date).format('ddd'),
    },
    components: {
      MonthCaption,
      Nav: HiddenNav,
    },
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={wrapperRef}
      className={`form-field-date-wrap udp-wrapper${error ? ' udp-error' : ''} udp-trigger-row${
        isOpen ? ' udp-trigger-row--focused' : ''
      }`}
    >
      {/* ── Compact DatesControls (no date selected yet) ─────────────────── */}
      {showCompact && (
        <div className="udp-compact-dates-controls" onClick={handleCompactDateClick}>
          <DatesControls
            label={<Text id="label.when_traveling" />}
            locale={locale}
            onDateClick={handleCompactDateClick}
            onTodayClick={() => handleQuickDate(0)}
            onTomorrowClick={() => handleQuickDate(1)}
            onMundialClick={handleMundialClick}
            showMundialButton={showMundialButton}
            showTomorrowWithMundial={showTomorrowWithMundial}
          />
        </div>
      )}

      {/* ── Value trigger (date is already selected) ──────────────────────── */}
      {!showCompact && (
        <Fragment>
          {/* Departure field */}
          <div
            className={`departureDate-wrapper${error ? ' field-error' : ''}`}
            onClick={() => handleInputClick(false)}
          >
            <label className={`label label-dates${error ? ' label-error' : ''}`}>
              <Text id="label.departure_date" />
            </label>

            <input
              type="text"
              readOnly
              className="udp-display-input"
              value={(departureDate === openTicketText ? openTicketLabel : departureDate) || ''}
              placeholder=" "
            />
          </div>

          {/* Return field — shown whenever there is a departure or return date */}
          {(departureDate || returnDate) && (
            <>
              <div className="returnDate-wrapper" onClick={() => handleInputClick(true)}>
                <label className="label label-dates">
                  <Text id="label.return_date" />
                </label>

                <input
                  type="text"
                  readOnly
                  className="udp-display-input"
                  value={(returnDate === openTicketText ? openTicketLabel : returnDate) || ''}
                  placeholder={returnPlaceholder}
                />
              </div>
            </>
          )}

          {/* Single clear button — clears both dates */}
          {(departureDate || returnDate) && (
            <div
              className="clean-place-input udp-clear-btn"
              onClick={handleCleanDeparture}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: selectIcon('cleanIcon', false) }}
            />
          )}

          {!departureDate && !returnDate && (
            <div
              className="iqdropdown-selection"
              onClick={() => handleInputClick(false)}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: selectIcon('arrow', false) }}
            />
          )}
        </Fragment>
      )}

      {/* ── Modal with tab buttons + calendar ────────────────────────────── */}
      {isOpen && (
        <Modal
          onClose={closeModal}
          title={<Text id="title.select_trip_type" />}
          contentClassName="udp-modal-content"
          modalClassName="udp-modal-unified"
          isFitContent
          showCloseButton
          footer={
            <div className="udp-continue-footer">
              {footerDeparture && (
                <div className="udp-continue-dates udp-continue-dates--mobile-only">
                  <p className="udp-continue-row">
                    <span className="udp-continue-label">
                      {modalTab === 'oneWay' ? (
                        <Text id="label.one_way" />
                      ) : (
                        <Text id="label.departure_date" />
                      )}
                    </span>{' '}
                    <span className="udp-continue-value">{footerDeparture}</span>
                  </p>
                  {(endDate || footerReturn) && (
                    <p className="udp-continue-row">
                      <span className="udp-continue-label">
                        <Text id="label.return_date" />
                      </span>{' '}
                      <span className="udp-continue-value">
                        {footerReturn || returnPlaceholder}
                      </span>
                    </p>
                  )}
                </div>
              )}
              <button
                type="button"
                className="udp-continue-btn"
                onClick={handleContinue}
                style={{ backgroundColor: buttonBg }}
              >
                <Text id={hasPassengersModal ? 'button.continue' : 'button.search'} />
              </button>
            </div>
          }
        >
          {/* Sentinel: when scrolled out of view the tabs are sticky */}
          {/* Tab selector */}
          <div className="udp-tabs">
            <button
              type="button"
              className={`udp-tab${modalTab === 'oneWay' ? ' active' : ''}`}
              onClick={() => handleTabChange('oneWay')}
            >
              <Text id="label.unified_one_way" />
            </button>

            <button
              type="button"
              className={`udp-tab${modalTab === 'round' ? ' active' : ''}`}
              onClick={() => handleTabChange('round')}
            >
              <Text id="label.unified_round" />
            </button>

            {showOpenTicket && (
              <button
                type="button"
                className={`udp-tab${modalTab === 'openTicket' ? ' active' : ''}`}
                onClick={() => handleTabChange('openTicket')}
              >
                <Text id="label.unified_open" />
              </button>
            )}
          </div>

          {/* Inline calendar – shared for Simple and Round */}
          {modalTab !== 'openTicket' && (
            <div className="udp-calendar-wrap">
              <DayPicker
                {...sharedDayPickerProps}
                components={{
                  ...sharedDayPickerProps.components,
                  DayButton: props => <DayButtonWithBadge {...props} badgeText={dayBadgeText} />,
                }}
                mode={modalTab === 'round' && !roundWithOpenSlot ? 'range' : 'single'}
                selected={
                  modalTab === 'round' && !roundWithOpenSlot
                    ? startDate || endDate
                      ? { from: startDate, to: endDate }
                      : undefined
                    : startDate || undefined
                }
                onSelect={
                  roundWithOpenSlot
                    ? handleRoundOpenSlotChange
                    : modalTab === 'round'
                    ? handleRangeChange
                    : handleSingleChange
                }
                resetOnSelect
              />
              {modalTab === 'round' && showOpenTicket && (startDate || departureIsOpen) && (
                <ReturnOpenSwitch
                  returnIsOpen={returnIsOpen}
                  handleToggleOpenReturn={handleToggleOpenReturn}
                  flatVariant={flatVariant}
                />
              )}
              <div className="udp-footer">
                <button
                  type="button"
                  className="udp-footer-btn udp-footer-btn--today"
                  onClick={handleTodayClick}
                >
                  <Text id="label.today" />
                </button>
                <button type="button" className="udp-footer-btn" onClick={closeModal}>
                  <Text id="button.close" />
                </button>
              </div>
            </div>
          )}

          {modalTab === 'openTicket' && (
            <div className="udp-open-ticket">
              <div className="udp-open-ticket__content">
                <div className="udp-open-ticket__text">
                  <div className="udp-open-ticket__header">
                    <span className="udp-open-ticket__title">
                      <Text id="label.open_ticket_title" />
                    </span>
                    <img
                      src={flatVariant ? flexibleCalendarFlatImg : flexibleCalendarImg}
                      className="udp-open-ticket__img"
                      alt=""
                      aria-hidden="true"
                    />
                  </div>
                  <div className="udp-open-ticket__divider" />
                  <div className="udp-open-ticket__description">
                    <span className="udp-open-ticket__subtitle">
                      <Text id="label.open_ticket_subtitle" />
                    </span>
                    <span className="udp-open-ticket__subtitle">
                      <Text id="label.open_ticket_redeem" />
                    </span>
                  </div>
                </div>
              </div>
              <ReturnOpenSwitch
                returnIsOpen={returnIsOpen}
                handleToggleOpenReturn={handleToggleOpenReturn}
                flatVariant={flatVariant}
              />
            </div>
          )}
        </Modal>
      )}
      {error && (
        <div
          className="icon-error"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: selectIcon('error', false) }}
        />
      )}
    </div>
  );
};

export default withText(() => ({
  returnPlaceholder: <Text id="placeholder.return_option" />,
  departureBadge: <Text id="label.departure_badge" />,
  openTicketLabel: <Text id="label.open_ticket_short" />,
}))(UnifiedDatePicker);
